import { motion } from 'framer-motion';
import { Award, Users, Heart } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useScrollReveal, staggerContainer, staggerItem } from '../hooks/useScrollReveal';

const About = () => {
  const { ref, isInView } = useScrollReveal();
  const { t } = useLanguage();

  return (
    <section id="about" className="section-padding bg-gradient-to-b from-black to-gray-900">
      <div className="container-max">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="reveal-section"
        >
          <motion.h2
            variants={staggerItem}
            className="text-4xl md:text-5xl font-bold text-center mb-16"
          >
            {t.about.title.includes(' ') ? (
              <>
                {t.about.title.split(' ')[0]} <span className="text-primary-400">{t.about.title.split(' ').slice(1).join(' ')}</span>
              </>
            ) : (
              <span className="text-primary-400">{t.about.title}</span>
            )}
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            {/* Text Content */}
            <motion.div variants={staggerItem} className="space-y-6">
              <p className="text-lg text-gray-300 leading-relaxed">
                {t.about.paragraph1}
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                {t.about.paragraph2}
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                {t.about.paragraph3}
              </p>
            </motion.div>

            {/* Image */}
            <motion.div
              variants={staggerItem}
              className="relative"
            >
              <img
                src="/IMG_2885.webp"
                alt="Martin Šenk training"
                className="w-full h-96 object-cover rounded-lg shadow-2xl"
                loading="lazy"
                width="576"
                height="768"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg"></div>
            </motion.div>
          </div>

          {/* Key Points */}
          <motion.div
            variants={staggerItem}
            className="grid md:grid-cols-3 gap-8 mb-16"
          >
            <div className="text-center">
              <div className="bg-primary-900/20 p-6 rounded-lg backdrop-blur-custom border border-primary-500/20 hover:border-primary-400/40 transition-all duration-300 hover:-translate-y-1">
                <Award className="w-12 h-12 text-primary-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">{t.about.keyPoint1Title}</h3>
                <p className="text-gray-300">
                  {t.about.keyPoint1Desc}
                </p>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-primary-900/20 p-6 rounded-lg backdrop-blur-custom border border-primary-500/20 hover:border-primary-400/40 transition-all duration-300 hover:-translate-y-1">
                <Users className="w-12 h-12 text-primary-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">{t.about.keyPoint2Title}</h3>
                <p className="text-gray-300">
                  {t.about.keyPoint2Desc}
                </p>
              </div>
            </div>

            <div className="text-center">
              <div className="bg-primary-900/20 p-6 rounded-lg backdrop-blur-custom border border-primary-500/20 hover:border-primary-400/40 transition-all duration-300 hover:-translate-y-1">
                <Heart className="w-12 h-12 text-primary-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">{t.about.keyPoint3Title}</h3>
                <p className="text-gray-300">
                  {t.about.keyPoint3Desc}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Additional Info */}
          <motion.div variants={staggerItem} className="text-center max-w-4xl mx-auto">
            <p className="text-lg text-gray-300 leading-relaxed mb-8">
              {t.about.paragraph4}
            </p>
            <p className="text-lg text-gray-300 leading-relaxed">
              {t.about.paragraph5}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
